import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Zap, Bell, Plus, Trash2, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface NotificationEmail {
  id: string;
  email: string;
  category: "all" | "budget_alerts" | "accepted_invoices";
}

const categoryLabels: Record<string, string> = {
  all: "All Notifications",
  budget_alerts: "Budget Alerts Only",
  accepted_invoices: "Accepted Invoices Only",
};

const categoryColors: Record<string, string> = {
  all: "bg-primary/10 text-primary border-primary/20",
  budget_alerts: "bg-warning/10 text-warning border-warning/20",
  accepted_invoices: "bg-success/10 text-success border-success/20",
};

export default function NotificationsSettings() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [emails, setEmails] = useState<NotificationEmail[]>([
    { id: "1", email: "ulathia@gmail.com", category: "all" },
  ]);
  const [newEmail, setNewEmail] = useState("");
  const [newCategory, setNewCategory] = useState<"all" | "budget_alerts" | "accepted_invoices">(
    "all"
  );

  const handleAdd = () => {
    if (!newEmail || !newEmail.includes("@")) return;
    setEmails((prev) => [
      ...prev,
      { id: Date.now().toString(), email: newEmail, category: newCategory },
    ]);
    setNewEmail("");
    setNewCategory("all");
    toast({
      title: "Email added",
      description: `${newEmail} will receive ${categoryLabels[newCategory].toLowerCase()}.`,
    });
  };

  const handleRemove = (id: string) => {
    setEmails((prev) => prev.filter((e) => e.id !== id));
    toast({ title: "Email removed" });
  };

  const handleCategoryChange = (
    id: string,
    category: "all" | "budget_alerts" | "accepted_invoices"
  ) => {
    setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, category } : e)));
    toast({ title: "Category updated" });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="w-full px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-primary">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-display font-bold text-foreground">Notifications</h1>
                <p className="text-sm text-muted-foreground">
                  Manage who receives email notifications
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-2xl space-y-6">
        <Card className="glass-card p-6">
          <h2 className="text-lg font-display font-semibold text-foreground mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Email Recipients
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Add email addresses and choose which notifications they should receive.
          </p>

          {/* Existing emails */}
          <div className="space-y-3 mb-6">
            {emails.map((entry) => (
              <div key={entry.id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm font-medium text-foreground flex-1 min-w-0 truncate">
                  {entry.email}
                </span>
                <Select
                  value={entry.category}
                  onValueChange={(v) => handleCategoryChange(entry.id, v as any)}
                >
                  <SelectTrigger className="w-44 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Notifications</SelectItem>
                    <SelectItem value="budget_alerts">Budget Alerts Only</SelectItem>
                    <SelectItem value="accepted_invoices">Accepted Invoices Only</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemove(entry.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {emails.length === 0 && (
              <p className="text-center text-muted-foreground py-4 text-sm">
                No notification emails configured
              </p>
            )}
          </div>

          {/* Add new */}
          <div className="border-t border-border pt-4">
            <Label className="text-sm font-medium mb-3 block">Add New Recipient</Label>
            <div className="flex items-center gap-2">
              <Input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="email@company.com"
                type="email"
                className="flex-1 h-9"
              />
              <Select value={newCategory} onValueChange={(v) => setNewCategory(v as any)}>
                <SelectTrigger className="w-44 h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Notifications</SelectItem>
                  <SelectItem value="budget_alerts">Budget Alerts Only</SelectItem>
                  <SelectItem value="accepted_invoices">Accepted Invoices Only</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" onClick={handleAdd} disabled={!newEmail || !newEmail.includes("@")}>
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
        </Card>

        {/* Category Legend */}
        <Card className="glass-card p-6">
          <h3 className="text-sm font-semibold text-foreground mb-3">Notification Categories</h3>
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <Badge className={`${categoryColors.all} border text-xs`}>All</Badge>
              <p className="text-sm text-muted-foreground">
                Receives budget alerts, accepted invoice notifications, and any other system emails.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Badge className={`${categoryColors.budget_alerts} border text-xs whitespace-nowrap`}>
                Budget Alerts
              </Badge>
              <p className="text-sm text-muted-foreground">
                Only receives alerts when the early payment budget is insufficient for pending
                offers.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Badge
                className={`${categoryColors.accepted_invoices} border text-xs whitespace-nowrap`}
              >
                Accepted Invoices
              </Badge>
              <p className="text-sm text-muted-foreground">
                Only receives notifications when a supplier accepts or declines an early payment
                offer.
              </p>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}
